import { useTranslation } from 'react-i18next';
import {
  type DepositWithdrawFiatType,
  type OperationMethodsDepositWithdrawFiatItem,
  type OperationType,
} from '@/shared/types';
import { cn } from '@/shared/utils';
import {
  ApiErrorText,
  Card,
  Loader,
  ModalComponentWrapper,
} from '@/shared/components';
import ChevronRightIcon from '@/shared/assets/chevron-right-icon.svg?react';
import { useUserScopeConfiguration } from '@/entities/user';
import {
  OPERATION_METHODS_CONFIG,
  type OperationMethod,
  type OperationMethodKey,
} from '../model';
import type { AssetGroup } from '@/entities/account';
import {
  useAppSettingsDictionary,
  useDepositWithdrawAssetsDictionary,
} from '@/shared/api';
import { useCallback, useEffect, useMemo } from 'react';

interface OperationMethodsModalProps {
  assetCode?: string;
  assetGroup?: AssetGroup;
  operationType: OperationType;
  onMethodSelect: (method: OperationMethod, assetCode?: string) => void;
  onClose?: () => void;
}

export function OperationMethodsModal({
  assetCode,
  assetGroup,
  operationType,
  onMethodSelect,
}: OperationMethodsModalProps) {
  const { appSettingsData } = useAppSettingsDictionary();
  const { t } = useTranslation();

  const {
    userScopeConfiguration,
    isUserScopeConfigurationLoading,
    userScopeConfigurationError,
  } = useUserScopeConfiguration();

  const {
    depositWithdrawAssetsData,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
  } = useDepositWithdrawAssetsDictionary();

  const isMethodAvailable = useCallback(
    (method: Omit<OperationMethod, 'isAvailable'>) => {
      if (!method.key || !userScopeConfiguration) return false;

      const [type, methodKey] = method.key.split('.') as [
        OperationType,
        string,
      ];

      const scope = userScopeConfiguration[type];
      if (!scope) return false;

      const isAvailableByScope =
        scope[methodKey as keyof typeof scope] ?? false;

      if (
        assetGroup === 'Fiat' &&
        (operationType === 'deposit' || operationType === 'withdraw')
      ) {
        const allowedMethods =
          depositWithdrawAssetsData?.value?.[operationType]?.fiat ?? [];

        const isAvailableByDictionary = allowedMethods.some(
          depositWithdrawFiatItem => {
            if (!Object.hasOwn(depositWithdrawFiatItem, methodKey)) {
              return false;
            }

            const assets = (
              depositWithdrawFiatItem as unknown as OperationMethodsDepositWithdrawFiatItem
            )[methodKey as DepositWithdrawFiatType];

            return assets.some(asset => asset.code === assetCode);
          }
        );

        return isAvailableByScope && isAvailableByDictionary;
      }

      return isAvailableByScope;
    },
    [
      assetGroup,
      operationType,
      userScopeConfiguration,
      depositWithdrawAssetsData,
    ]
  );

  const addAdditionalText = (methodKey: OperationMethodKey) => {
    switch (methodKey) {
      case 'deposit.card': {
        const { displayType, buttonText } =
          appSettingsData?.value?.depositWithdrawInformation ?? {};
        return displayType === 'button' ? buttonText : undefined;
      }
    }
    return undefined;
  };

  const availableMethods = useMemo(() => {
    const methods = OPERATION_METHODS_CONFIG[operationType] ?? [];

    return methods
      .map(method => ({
        ...method,
        additionalText: method.additionalText
          ? addAdditionalText(method.key)
          : undefined,
        isAvailable: isMethodAvailable(method),
      }))
      .filter(method => method.isAvailable);
  }, [operationType, isMethodAvailable, addAdditionalText]);

  useEffect(() => {
    if (availableMethods.length === 1) {
      onMethodSelect(availableMethods[0]);
    }
  }, [availableMethods, onMethodSelect]);

  const isLoading =
    isUserScopeConfigurationLoading ||
    depositWithdrawAssetsLoading ||
    availableMethods.length === 0;

  const hasError =
    userScopeConfigurationError ||
    depositWithdrawAssetsError ||
    !userScopeConfiguration ||
    !depositWithdrawAssetsData;

  return (
    <ModalComponentWrapper
      title={t(`${operationType}Methods.title`)}
      description={`${t(`${operationType}Methods.description`)}:`}
      descriptionClassName="p4"
      headerClassName="bg-secondary-bg/50 text-secondary-bg">
      {isLoading && <Loader />}

      {hasError && (
        <ApiErrorText
          error={userScopeConfigurationError || depositWithdrawAssetsError}
          size="small"
        />
      )}

      {!isLoading && !hasError && (
        <div className="space-y-[1.6rem]">
          {Boolean(availableMethods.length) &&
            availableMethods.map(method => (
              <Card
                className={cn(
                  'flex flex-row items-center gap-[1.6rem] px-[2.4rem] py-[1.6rem] min-h-[6.8rem] rounded-2xl cursor-pointer'
                )}
                key={method.key}
                onClick={() => onMethodSelect(method, assetCode)}>
                <div className="size-[3.6rem] flex items-center justify-center">
                  {method.iconComponent &&
                    (typeof method.iconComponent === 'string' ? (
                      <img
                        src={method.iconComponent}
                        alt=""
                        className="max-w-[100%] max-h-[2.4rem]"
                      />
                    ) : (
                      <method.iconComponent className="max-w-[100%] h-[2.4rem]" />
                    ))}
                </div>

                <div className="flex-1">
                  <div className="headline4 text-foreground flex flex-col">
                    {t(`${method.translationKey}.name`)}
                    {method.additionalText && (
                      <span className={'p4 text-muted-foreground'}>
                        {method.additionalText}
                      </span>
                    )}
                  </div>
                </div>

                <div className="group-hover:text-primary transition-colors text-tertiary">
                  <ChevronRightIcon className="w-[2.4rem] h-[2.4rem]" />
                </div>
              </Card>
            ))}
        </div>
      )}
    </ModalComponentWrapper>
  );
}
